import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import "./Filters.sass";

const CONTENT_SELECTOR = "#leads-filter-content";

const offsetWithin = (el, container) =>
  el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;

const LeadsFilterNav = ({ sections }) => {
  const [expanded, setExpanded] = useState(() => (sections[0] ? { [sections[0].key]: true } : {}));
  const [activeItem, setActiveItem] = useState(() => sections[0]?.items?.[0]?.key || null);

  const itemToSection = useMemo(() => {
    const map = {};
    sections.forEach((section) =>
      section.items.forEach((item) => {
        map[item.key] = section.key;
      }),
    );
    return map;
  }, [sections]);

  const scrollToId = (anchorId) => {
    const el = document.getElementById(anchorId);
    const container = document.querySelector(CONTENT_SELECTOR);
    if (!el || !container) return;
    const top = offsetWithin(el, container);
    container.scrollTo({ top, behavior: "smooth" });
  };

  // Sections toggle independently — multiple can be open at once.
  const toggleSection = (section) => {
    const willExpand = !expanded[section.key];
    setExpanded((prev) => ({ ...prev, [section.key]: willExpand }));
    if (willExpand) scrollToId(`section-${section.key}`);
  };

  const selectItem = (item) => {
    const sectionKey = itemToSection[item.key];
    setExpanded((prev) => ({ ...prev, [sectionKey]: true }));
    setActiveItem(item.key);
    scrollToId(`section-${sectionKey}`);
  };

  return (
    <nav className="leads-filter-nav">
      {sections.map((section) => {
        const isOpen = Boolean(expanded[section.key]);
        return (
          <div className="leads-filter-nav__section" key={section.key}>
            <button
              type="button"
              className="leads-filter-nav__heading"
              aria-expanded={isOpen}
              onClick={() => toggleSection(section)}
            >
              <span>{section.label}</span>
              <i
                className={`fa6-solid ${isOpen ? "fa6-chevron-down" : "fa6-chevron-right"}`}
                aria-hidden="true"
              />
            </button>
            {isOpen && (
              <div className="leads-filter-nav__items">
                {section.items.map((item) => (
                  <button
                    type="button"
                    key={item.key}
                    className={`leads-filter-nav__tab${activeItem === item.key ? " is-active" : ""}`}
                    onClick={() => selectItem(item)}
                  >
                    <i className={item.icon} aria-hidden="true" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

LeadsFilterNav.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          icon: PropTypes.string,
        }),
      ).isRequired,
    }),
  ).isRequired,
};

export default LeadsFilterNav;
